import { CancelledError } from "@tanstack/react-query"
import { z } from "zod"

import i18n from "@/i18n/config"
import { isWtpFormatValid, WTP_NUMBER_FORMAT } from "@/utils/wtpUtils"

const t = i18n.t

interface ValidateWtpResponse {
  isWtpValid: boolean
  isWtpDuplicate: boolean
  isWtpOutDated: boolean
}

export const getWtpValidationError = async (
  wtpNumber: string | undefined,
  validateWtpAsync?: (wtpNumber: string) => Promise<ValidateWtpResponse>
): Promise<string | null> => {
  if (!wtpNumber || wtpNumber.length === 0) {
    return t("passHolderDetails.form.errors.wtpNumberRequired")
  }

  if (!isWtpFormatValid(wtpNumber)) {
    return t("passHolderDetails.form.errors.wtpNumberFormat", {
      format: WTP_NUMBER_FORMAT,
    })
  }

  if (validateWtpAsync) {
    try {
      const result = await validateWtpAsync(wtpNumber)

      if (!result.isWtpValid) {
        return t("passHolderDetails.form.errors.wtpInvalid")
      }
      if (result.isWtpDuplicate) {
        return t("passHolderDetails.form.errors.wtpDuplicate")
      }
      if (result.isWtpOutDated) {
        return t("passHolderDetails.form.errors.wtpOutDated")
      }
    } catch (error) {
      if (error instanceof CancelledError) {
        return null
      }
      return t("passHolderDetails.form.errors.wtpFetchError")
    }
  }

  return null
}

export const createWtpNumberValidationRefinement = (
  validateWtpAsync?: (wtpNumber: string) => Promise<ValidateWtpResponse>
) => {
  return async (
    data: { passSelection: string; wtpNumber?: string },
    ctx: z.RefinementCtx
  ) => {
    if (data.passSelection !== "existing") {
      return
    }
    const error = await getWtpValidationError(data.wtpNumber, validateWtpAsync)

    if (error) {
      ctx.addIssue({
        code: "custom",
        path: ["wtpNumber"],
        message: error,
      })
    }
  }
}
